import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useEffect, useState } from 'react';
import { node, oneOfType, arrayOf, number, bool, func, string } from 'prop-types';
import useCarousel from './useCarousel';
import { getNewIndex } from './reducer';
import { ArrowsControlsButton, Container, CarouselContent, CarouselItem, CarouselIndicators, CarouselIndicator, CarouselContainer, PrevIcon, NextIcon, SlideCount } from './carousel.styles';

const Carousel = _ref => {
  let {
    children,
    slidesPerView,
    showPartial,
    showPartialNextSlide,
    interval,
    transitionDuration,
    auto,
    showArrowControls,
    showControls,
    showSlideCount,
    infinity,
    insideControls,
    onChange,
    gutter,
    itemHeight,
    isProductCarousel,
    lazy,
    goTo,
    verticalAlign
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "slidesPerView", "showPartial", "showPartialNextSlide", "interval", "transitionDuration", "auto", "showArrowControls", "showControls", "showSlideCount", "infinity", "insideControls", "onChange", "gutter", "itemHeight", "isProductCarousel", "lazy", "goTo", "verticalAlign"]);

  const slides = React.Children.toArray(children);
  const length = slides == null ? void 0 : slides.length;
  const numActive = Math.min(length, slidesPerView);
  const {
    handlers,
    style,
    cardWidth,
    indicators,
    setActive,
    active,
    visible
  } = useCarousel({
    length,
    interval,
    transitionDuration,
    auto,
    slidesPerView: numActive,
    showPartial,
    showPartialNextSlide,
    lazy
  });
  const [slideIndex, setSlideIndex] = useState(0);
  useEffect(() => {
    setSlideIndex(goTo);
    setActive(goTo);
  }, [goTo]);
  useEffect(() => {
    onChange(active);
  }, [active]);
  return length > 1 ? /*#__PURE__*/React.createElement(Container, _extends({
    "data-testid": "carousel"
  }, props), showArrowControls && /*#__PURE__*/React.createElement(ArrowsControlsButton, {
    "data-testid": "arrow-previous-button",
    "aria-label": "Ver elemento da esquerda",
    disabled: !infinity && slideIndex - 1 < 0,
    left: true,
    insideControls: insideControls,
    onClick: () => {
      const newIndex = getNewIndex({
        direction: 'previous',
        slides,
        slideIndex,
        showSlideCount,
        infinity
      });
      setSlideIndex(newIndex);
      setActive(newIndex);
    }
  }, /*#__PURE__*/React.createElement(PrevIcon, null)), /*#__PURE__*/React.createElement(CarouselContainer, {
    showArrowControls: showArrowControls,
    insideControls: insideControls
  }, /*#__PURE__*/React.createElement(CarouselContent, _extends({
    showControls: showControls,
    showSlideCount: showSlideCount,
    insideControls: insideControls,
    itemHeight: itemHeight
  }, handlers, {
    style: style
  }), slides.map((slide, index) => /*#__PURE__*/React.createElement(CarouselItem, {
    "data-testid": "carousel-item",
    width: cardWidth,
    isProductCarousel: isProductCarousel,
    gutter: showPartial || showPartialNextSlide || slidesPerView > 1 ? gutter : 0,
    alignContent: verticalAlign,
    index: index,
    key: `carousel-item-${slides.length}-${index + 1}`
  }, visible.includes(index) && slide))), showControls && slides.length > numActive && /*#__PURE__*/React.createElement(CarouselIndicators, {
    insideControls: insideControls,
    "data-testid": "carousel-indicators"
  }, Array(indicators).fill().map((slide, index) => /*#__PURE__*/React.createElement(CarouselIndicator, {
    "data-testid": "carousel-indicator",
    active: active / slidesPerView === index,
    onClick: () => {
      setActive(index);
      setSlideIndex(index);
    },
    key: `carousel-indicator-${slides.length}-${index + 1}`
  }))), showSlideCount && /*#__PURE__*/React.createElement(SlideCount, {
    "data-testid": "slide-count"
  }, Math.min(slideIndex + 1, length), " / ", length)), showArrowControls && /*#__PURE__*/React.createElement(ArrowsControlsButton, {
    "data-testid": "arrow-next-button",
    "aria-label": "Ver elemento da direita",
    disabled: !infinity && slideIndex + 1 === indicators,
    insideControls: insideControls,
    onClick: () => {
      const newIndex = getNewIndex({
        direction: 'next',
        slides,
        slideIndex,
        showSlideCount,
        infinity
      });
      setSlideIndex(newIndex);
      setActive(newIndex);
    },
    right: true
  }, /*#__PURE__*/React.createElement(NextIcon, null))) : children;
};

Carousel.defaultProps = {
  slidesPerView: 1,
  showPartial: false,
  showPartialNextSlide: false,
  interval: 500,
  transitionDuration: 400,
  auto: true,
  showArrowControls: false,
  showControls: true,
  showSlideCount: false,
  onChange: () => {},
  gutter: 4,
  infinity: false,
  isProductCarousel: false,
  insideControls: false,
  lazy: false,
  goTo: 0,
  verticalAlign: ''
};
process.env.NODE_ENV !== "production" ? Carousel.propTypes = {
  children: oneOfType([arrayOf(node), node]).isRequired,
  slidesPerView: number,
  interval: number,
  transitionDuration: number,
  auto: bool,
  showArrowControls: bool,
  showPartial: bool,
  showPartialNextSlide: bool,
  showControls: bool,
  showSlideCount: bool,
  onChange: func,
  gutter: number,
  itemHeight: number,
  infinity: bool,
  isProductCarousel: bool,
  insideControls: bool,
  lazy: bool,
  goTo: number,
  verticalAlign: string
} : void 0;
Carousel.CarouselItem = CarouselItem;
export default Carousel;